import { Button } from "@mui/material";
import { getAllConversationData } from "../utility/getAllConversationData";
import { useAuth } from "../../context/AuthContext";
import { getAllUsersData } from "../utility/getAllUsersData";
import * as XLSX from "xlsx";
import { useState } from "react";
import { set } from "date-fns";

export const GetAllConversationDataComponent = () => {
  const { idToken, refreshAuthSession } = useAuth();
  const [isProgress, setIsProgress] = useState<boolean>(false);

  const downloadConversationData = async () => {
    if (!idToken) return;
    setIsProgress(true);

    const conversationData = await getAllConversationData(idToken);
    const userData = await getAllUsersData(idToken);

    // console.log("=== デバッグ: 取得データ ===");
    // console.log("conversationData件数:", conversationData.data.length);
    // console.log("conversationDataサンプル:", conversationData.data.slice(0, 2));
    // console.log("userData件数:", userData.users.length);
    // console.log("userDataサンプル:", userData.users.slice(0, 2));

    // UUIDとメールアドレスのマッピングを作成
    const uuidToEmail: { [key: string]: string } = {};
    userData.users.forEach((user: any) => {
      uuidToEmail[user.username] = user.attributes.email;
    });

    // console.log("=== デバッグ: UUIDマッピング ===");
    // console.log("マッピング件数:", Object.keys(uuidToEmail).length);
    // console.log("マッピングサンプル:", Object.entries(uuidToEmail).slice(0, 3));

    // UUIDごとにデータをグループ化
    const groupedData: { [uuid: string]: any[] } = {};
    conversationData.data.forEach((conversation: any) => {
      const conversationId = conversation.conversationId;
      // console.log("処理中のconversationId:", conversationId);

      // conversationIdの形式: uuid-YYYYMMDD-randomString
      // 最後のハイフンの位置を見つけて、そこから日付部分を抽出
      const parts = conversationId.split("-");

      // UUIDは5つのパートからなる（例: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx）
      // 日付は次のパート、残りはランダム文字列
      const uuid = parts.slice(0, 5).join("-"); // 最初の5パートがUUID
      const dateStr = parts[5]; // 6番目のパートが日付

      // console.log("抽出したUUID:", uuid);
      // console.log("抽出した日付:", dateStr);

      if (!groupedData[uuid]) {
        groupedData[uuid] = [];
      }
      groupedData[uuid].push({
        conversationId: conversationId,
        date: dateStr,
        messages: conversation.messages,
      });
    });

    // console.log("=== デバッグ: グループ化データ ===");
    // console.log("グループ化されたUUID数:", Object.keys(groupedData).length);
    // Object.entries(groupedData).forEach(([uuid, convs]) => {
    //   console.log(`UUID: ${uuid}`);
    //   console.log(`  - メールアドレス: ${uuidToEmail[uuid]}`);
    //   console.log(`  - 会話数: ${convs.length}`);
    //   console.log(
    //     `  - 日付一覧:`,
    //     convs.map((c) => c.date)
    //   );
    // });

    // Excelワークブックを作成
    const workbook = XLSX.utils.book_new();

    // UUID毎に処理
    Object.entries(groupedData).forEach(([uuid, conversations]) => {
      // console.log(`\n=== デバッグ: ${uuid} の処理開始 ===`);
      // console.log("会話数:", conversations.length);

      // 日付でソート
      conversations.sort((a, b) => a.date.localeCompare(b.date));

      // console.log(
      //   "ソート後の日付:",
      //   conversations.map((c) => c.date)
      // );

      // 最も古い日付を取得
      const baseDate = conversations[0].date;
      // console.log("基準日付 (baseDate):", baseDate);

      // 日付をdayXに変換
      const dateMapping: { [date: string]: string } = {};
      conversations.forEach((conv) => {
        const dayDiff = calculateDayDifference(baseDate, conv.date);
        dateMapping[conv.date] = `day${dayDiff + 1}`;
        // console.log(
        //   `日付変換: ${conv.date} -> day${dayDiff + 1} (差分: ${dayDiff}日)`
        // );
      });

      // シートデータを作成
      const sheetData: any[][] = [];
      const uniqueDates = Array.from(
        new Set(conversations.map((c) => c.date))
      ).sort();

      // console.log("ユニークな日付:", uniqueDates);

      // ヘッダー行（日付）
      const header = ["", ...uniqueDates.map((date) => dateMapping[date])];
      sheetData.push(header);
      // console.log("ヘッダー行:", header);

      // 各日付の最大メッセージ数を取得
      const maxMessages = Math.max(
        ...conversations.map((c) => c.messages.length)
      );
      // console.log("最大メッセージ数:", maxMessages);

      // 各メッセージ行を作成
      for (let i = 0; i < maxMessages; i++) {
        const row: any[] = [];

        // 最初の列にroleを追加
        const firstConv = conversations.find((c) => c.messages[i]);
        row.push(firstConv?.messages[i]?.role || "");

        // 各日付のメッセージを追加
        uniqueDates.forEach((date) => {
          const conv = conversations.find((c) => c.date === date);
          const message = conv?.messages[i];
          row.push(message?.content || "");
        });

        sheetData.push(row);
      }

      // console.log(`シートデータ行数: ${sheetData.length}`);

      // シートを作成
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      // シート名をメールアドレスに設定（無効な文字を置換）
      const email = uuidToEmail[uuid];
      // console.log(`UUID: ${uuid} のメールアドレス: ${email}`);

      const sheetName = (email || uuid)
        .replace(/[:\\\/\?\*\[\]]/g, "_")
        .substring(0, 31); // Excelのシート名は31文字まで

      // console.log("シート名:", sheetName);

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Excelファイルをダウンロード
    const filename = `conversation_data_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    console.log("ファイル名:", filename);
    XLSX.writeFile(workbook, filename);
    console.log("ダウンロード完了");
    setIsProgress(false);
  };

  // YYYYMMDD形式の日付文字列から日数差を計算
  const calculateDayDifference = (
    baseDate: string,
    targetDate: string
  ): number => {
    // console.log(`日付差分計算: baseDate=${baseDate}, targetDate=${targetDate}`);

    if (
      !baseDate ||
      !targetDate ||
      baseDate.length !== 8 ||
      targetDate.length !== 8
    ) {
      console.error("無効な日付形式:", { baseDate, targetDate });
      return 0;
    }

    const base = new Date(
      parseInt(baseDate.substring(0, 4)),
      parseInt(baseDate.substring(4, 6)) - 1,
      parseInt(baseDate.substring(6, 8))
    );
    const target = new Date(
      parseInt(targetDate.substring(0, 4)),
      parseInt(targetDate.substring(4, 6)) - 1,
      parseInt(targetDate.substring(6, 8))
    );

    // console.log(
    //   `Date変換: base=${base.toISOString()}, target=${target.toISOString()}`
    // );

    const diff = Math.floor(
      (target.getTime() - base.getTime()) / (1000 * 60 * 60 * 24)
    );

    // console.log(`計算結果: ${diff}日`);
    return diff;
  };

  //JSON形式でのDL
  const downloadConversationDataAsJson = async () => {
    if (!idToken) return;
    setIsProgress(true);

    const conversationData = await getAllConversationData(idToken);
    const userData = await getAllUsersData(idToken);

    // UUIDとメールアドレスのマッピングを作成
    const uuidToEmail: { [key: string]: string } = {};
    userData.users.forEach((user: any) => {
      uuidToEmail[user.username] = user.attributes.email;
    });

    // UUIDごとにデータをグループ化
    const groupedData: { [uuid: string]: any[] } = {};
    conversationData.data.forEach((conversation: any) => {
      const conversationId = conversation.conversationId;
      const parts = conversationId.split("-");
      const uuid = parts.slice(0, 5).join("-");
      const dateStr = parts[5];

      if (!groupedData[uuid]) {
        groupedData[uuid] = [];
      }
      groupedData[uuid].push({
        conversationId: conversationId,
        date: dateStr,
        messages: conversation.messages,
      });
    });

    // JSON形式のデータを作成
    const jsonData: any[] = [];

    Object.entries(groupedData).forEach(([uuid, conversations]) => {
      // 日付でソート
      conversations.sort((a, b) => a.date.localeCompare(b.date));

      // 最も古い日付を取得
      const baseDate = conversations[0].date;

      // 日付をdayXに変換
      const dateMapping: { [date: string]: string } = {};
      conversations.forEach((conv) => {
        const dayDiff = calculateDayDifference(baseDate, conv.date);
        dateMapping[conv.date] = `day${dayDiff + 1}`;
      });

      const email = uuidToEmail[uuid] || uuid;

      // ユーザーごとのデータを構築
      const userData: any = {
        email: email,
        uuid: uuid,
        conversations: conversations.map((conv) => ({
          day: dateMapping[conv.date],
          date: conv.date,
          messages: conv.messages.map((msg: any) => ({
            role: msg.role,
            content: msg.content,
          })),
        })),
      };

      jsonData.push(userData);
    });

    // JSONファイルとしてダウンロード
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation_data_${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log("JSONダウンロード完了");
    setIsProgress(false);
  };

  return (
    <>
      <Button variant="contained" onClick={downloadConversationData}>
        チャットデータ(EXCEL)
      </Button>
      <Button
        sx={{ ml: 2 }}
        variant="contained"
        onClick={downloadConversationDataAsJson}
      >
        チャットデータ(JSON)
      </Button>
      {isProgress && <p>ダウンロード中...</p>}
    </>
  );
};
